import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { useSecretCodeToggle } from '../useSecretCodeToggle';

function createColorThemeContext() {
  type ColorTheme = 'light' | 'dark';

  type ColorThemeContextValue = {
    colorTheme: ColorTheme;
    setColorTheme: (theme: ColorTheme) => void;
    toggleColorTheme: () => void;
  };

  const STORAGE_KEY = 'lp-color-theme';
  const ColorThemeContent = createContext<ColorThemeContextValue>({
    colorTheme: 'light',
    setColorTheme: () => undefined,
    toggleColorTheme: () => undefined,
  });
  const ColorThemeContentProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const cheatDarkModeEnabled = useSecretCodeToggle('DARKMODE');
    const [baseTheme, setBaseTheme] = useState<ColorTheme>(() => {
      if (typeof window === 'undefined') {
        return 'light';
      }

      const storedTheme = window.localStorage.getItem(STORAGE_KEY);
      return storedTheme === 'dark' ? 'dark' : 'light';
    });

    useEffect(() => {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, baseTheme);
      }
    }, [baseTheme]);

    const colorTheme = cheatDarkModeEnabled ? (baseTheme === 'dark' ? 'light' : 'dark') : baseTheme;

    const value = useMemo<ColorThemeContextValue>(
      () => ({
        colorTheme,
        setColorTheme: setBaseTheme,
        toggleColorTheme: () => setBaseTheme((prev) => (prev === 'dark' ? 'light' : 'dark')),
      }),
      [colorTheme],
    );

    return <ColorThemeContent.Provider value={value}>{children}</ColorThemeContent.Provider>;
  };
  function useColorTheme() {
    return useContext(ColorThemeContent);
  }
  return { ColorThemeContentProvider, useColorTheme };
}

export const { ColorThemeContentProvider, useColorTheme } = createColorThemeContext();
